<div class="mb-6 flex flex-wrap items-start justify-between gap-4">
  <div>
    <p-button
      label="All Auto Molds"
      icon="pi pi-arrow-left"
      severity="secondary"
      [text]="true"
      class="!-ml-2 !mb-1"
      (onClick)="goBack()"
    />
    <div class="relative mb-2 flex w-full items-center gap-2">
      @if (isEditingFlowName()) {
        <input
          #flowNameInput
          pInputText
          type="text"
          class="min-w-0 flex-1 font-semibold leading-tight"
          [value]="flowName()"
          [placeholder]="flowIdLabel"
          maxlength="255"
          (keydown.enter)="$event.preventDefault(); submitFlowNameEdit()"
        />
        <p-button
          label="Save"
          icon="pi pi-check"
          (onClick)="submitFlowNameEdit()"
        />
        <p-button
          label="Cancel"
          severity="secondary"
          [text]="true"
          (onClick)="cancelFlowNameEdit()"
        />
      } @else {
        <h1 class="!mt-0 !mb-0">{{ flowName() || "" }}</h1>
        <p-button
          icon="pi pi-pencil"
          size="large"
          severity="secondary"
          [text]="true"
          ariaLabel="Edit flow name"
          (onClick)="beginFlowNameEdit()"
          pTooltip="Edit flow name"
          tooltipPosition="top"
        />
      }
    </div>
    <p class="!mt-0 text-muted-color">
      Design, configure, and run the steps in your automated mold workflow.
    </p>
  </div>
  <div class="flex items-center gap-3">
    @if (isLoading()) {
      <span class="text-sm text-muted-color">Loading...</span>
    } @else if (autosaveStatus() === "saving") {
      <span class="text-sm text-muted-color">
        <i class="pi pi-spin pi-spinner mr-1"></i>Saving...
      </span>
    } @else if (autosaveStatus() === "saved") {
      <span class="text-sm text-muted-color">
        <i class="pi pi-check mr-1"></i>Saved
      </span>
    } @else if (autosaveStatus() === "failed") {
      <span class="text-sm text-red-600">
        <i class="pi pi-exclamation-circle mr-1"></i>Save failed
      </span>
    }
    <label
      for="runDownstream"
      class="flex cursor-pointer items-center gap-3 rounded-lg border border-primary px-3 py-2"
    >
      <div class="flex items-center gap-1.5">
        <span class="font-medium text-primary"> Run Downstream Steps </span>
        <i
          class="pi pi-info-circle cursor-help text-primary"
          [pTooltip]="runDownstreamHelpText"
          tooltipPosition="bottom"
          aria-label="Run downstream steps help"
        ></i>
      </div>
      <p-toggleswitch
        inputId="runDownstream"
        [ngModel]="runDownstreamEnabled()"
        (ngModelChange)="setRunDownstreamEnabled($event)"
      />
    </label>
    <p-button
      label="Add Flow"
      icon="pi pi-sitemap"
      severity="secondary"
      [outlined]="true"
      (onClick)="openAddFlowDialog()"
    />
    <p-button
      label="Add Node"
      icon="pi pi-plus"
      severity="secondary"
      [outlined]="true"
      (onClick)="isAddNodeDrawerVisible.set(true)"
    />
  </div>
</div>

@if (error(); as message) {
  <p-message severity="warn" [text]="message" class="mb-4 block" />
}

<p-drawer
  [(visible)]="isAddNodeDrawerVisible"
  appendTo="body"
  header="Add Node"
  [modal]="false"
  position="right"
  [styleClass]="'!w-full sm:!w-96'"
>
  <div class="flex flex-col gap-2">
    @for (kind of nodeKinds; track kind) {
      <p-button
        [label]="getNodeLabel(kind)"
        [icon]="getNodeIcon(kind)"
        [outlined]="true"
        styleClass="w-full justify-start"
        (onClick)="addNode(kind)"
      />
    }
  </div>
</p-drawer>

<p-dialog
  [visible]="isAddFlowDialogVisible()"
  (visibleChange)="isAddFlowDialogVisible.set($event)"
  [modal]="true"
  [style]="{ width: '640px', maxWidth: '96vw' }"
  [draggable]="false"
  [resizable]="false"
  appendTo="body"
>
  <ng-template pTemplate="header">
    <span class="text-lg font-semibold text-surface-switch-900">Add Flow</span>
  </ng-template>

  <p class="mb-4 text-sm text-muted-color">
    Select a flow to add beside the current graph. Step runs are not copied.
  </p>
  @if (isLoadingImportableFlows()) {
    <p class="text-muted-color">Loading flows...</p>
  } @else if (importableFlows().length === 0) {
    <p class="text-muted-color">No other flows are available.</p>
  } @else {
    <div
      class="max-h-[300px] overflow-y-auto rounded-lg border border-surface-switch-200"
    >
      @for (flow of importableFlows(); track flow.id) {
        <label
          [class]="
            'flex cursor-pointer items-center gap-3 border-b border-surface-switch-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-switch-50 ' +
            (selectedImportFlow()?.id === flow.id ? 'bg-primary/5' : '')
          "
        >
          <p-radioButton
            name="importFlow"
            [value]="flow.id"
            [ngModel]="selectedImportFlow()?.id"
            (ngModelChange)="selectImportFlow(flow)"
          />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-surface-switch-900">
              {{ flow.name }}
            </span>
            <span class="text-sm text-muted-color">AM-{{ flow.id }}</span>
          </div>
        </label>
      }
    </div>
  }

  <div class="mt-4 flex justify-end gap-2">
    <button
      type="button"
      (click)="isAddFlowDialogVisible.set(false)"
      class="rounded-md border border-surface-switch-300 bg-surface-switch-0 px-4 py-2 text-sm font-medium text-surface-switch-700 transition-colors hover:bg-surface-switch-100"
    >
      Cancel
    </button>
    <button
      type="button"
      (click)="importSelectedFlow()"
      [disabled]="selectedImportFlow() === null"
      class="rounded-md border border-primary bg-primary px-4 py-2 text-sm font-medium text-primary-contrast transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
    >
      Add Flow
    </button>
  </div>
</p-dialog>

<app-moulding-node-dialog-host />

<div class="relative">
  <div
    #reteContainer
    class="rete-container rounded-xl border border-surface-switch-200 bg-surface-switch-0"
  ></div>
  @if (!isLoading() && nodeCount() === 0) {
    <div
      class="pointer-events-none absolute inset-0 flex items-center justify-center p-6 text-center text-muted-color text-lg"
    >
      This flow is currently empty. Click “Add Node” to get started.
    </div>
  }
  <div
    class="absolute bottom-4 left-4 z-10 flex items-center gap-1 rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-1 shadow-sm"
  >
    <p-button
      icon="pi pi-minus"
      severity="secondary"
      [text]="true"
      [disabled]="isLoading() || !canZoomOut"
      ariaLabel="Zoom out"
      pTooltip="Zoom out"
      tooltipPosition="top"
      (onClick)="zoomOut()"
    />
    <span
      class="min-w-14 px-1 text-center text-sm font-medium text-surface-switch-900"
      aria-live="polite"
    >
      {{ canvasZoomPercent() }}%
    </span>
    <p-button
      icon="pi pi-plus"
      severity="secondary"
      [text]="true"
      [disabled]="isLoading() || !canZoomIn"
      ariaLabel="Zoom in"
      pTooltip="Zoom in"
      tooltipPosition="top"
      (onClick)="zoomIn()"
    />
  </div>
</div>
